import React, { useState } from 'react';
import styles from './Resources360.module.scss';
import { Button } from 'new/elements/Button';
import { MenuItem, Typography } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { User } from 'new/entities/User';
import { Select } from 'new/elements/Select';
import { useDispatch } from 'react-redux';
import { updateAcl, updateMultipleAcl } from 'js/actions';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { UPDATE_ACL_REQUEST } from 'js/constants/actionTypes';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { ACL_RESOURCE_TYPE_DF_DATASET } from 'js/constants/aclConstants';
import { aclTypes } from 'new/components/collaborate/ResourceDetail/ResourceDetail';
import { Stack } from 'new/elements/Stack';
import { getEffectiveRole } from 'js/lib/ACLUtils';
import { AclEntitySelect } from 'new/components/collaborate/AclEntitySelect';
import flowTypes from 'new/components/flows/types/flowTypes';
import { Banner } from 'new/elements/Banner/Banner';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';

interface AccessProps {
  data: any;
  resourceId: string;
  resourceType: string;
  alreadyShared?: User[];
  externalSelection?: User[];
}

// Matching AclTabView.js ACL_SETTINGS_MAPPING
export const ACL_SETTINGS_MAPPING = {
  owner: {
    key: 'owner',
    title: 'Owner',
    value: 'owner',
    description:
      'Resource creator/owner. This user has full administrative rights to this resource. Contact support@nexla.com for transfer of ownership.',
  },
  collaborator: {
    key: 'collaborator',
    title: 'Collaborator (Read-only)',
    value: 'collaborator',
    description:
      'Able to view all information about this resource and use it in other Flows or Nexsets, but should not be able to modify it.',
  },
  operator: {
    key: 'operator',
    title: 'Operator',
    value: 'operator',
    description: 'Same as collaborator, but can also pause/activate resources.',
  },
  admin: {
    key: 'admin',
    title: 'Administrator',
    value: 'admin',
    description: 'Full administrative rights to this resource.',
  },
};

// Options for assigning new access (matches AclTabView.js ACL_SETTINGS_OPTIONS)
const ACL_ASSIGNMENT_OPTIONS = [
  ACL_SETTINGS_MAPPING['collaborator'],
  ACL_SETTINGS_MAPPING['operator'],
  ACL_SETTINGS_MAPPING['admin'],
];

const renderSelectItem = (item) => {
  return (
    <MenuItem key={item.value} className={styles.selectOption} value={item.value}>
      <Typography>{item.title}</Typography>
      <Typography variant="caption">{item.description}</Typography>
    </MenuItem>
  );
};

/**
 * AclItem component - matches functionality of AclTabView._renderAclItem
 * - Shows name/email properly for USER, ORG, TEAM types
 * - Role dropdown for non-sharer roles (allows changing role)
 * - Delete icon to revoke access
 * - Read-only mode hides edit/delete controls
 */
const AclItem = ({ aclItem, data, aclType, resourceType, isNexset, isReadOnly }) => {
  const dispatch = useDispatch();
  const role = getEffectiveRole(aclItem);

  // Get display name based on type (matching AclTabView.js logic)
  const getDisplayInfo = () => {
    switch (aclItem.type) {
      case 'USER':
        return {
          title: aclItem.full_name || aclItem.email,
          subtitle: aclItem.email,
        };
      case 'ORG':
        return {
          title: 'Entire organization',
          subtitle: `Everyone in ${aclItem.name || 'the'} organization`,
        };
      case 'TEAM':
        return {
          title: aclItem.name,
          subtitle: 'Team',
        };
      default:
        return {
          title: aclItem.full_name || aclItem.name || aclItem.email,
          subtitle: aclItem.email || aclItem.description,
        };
    }
  };

  const displayInfo = getDisplayInfo();

  // For Nexset sharers, use DF_DATASET to match AclTabView behavior (enables org_id handling)
  const effectiveResourceType = isNexset ? ACL_RESOURCE_TYPE_DF_DATASET : resourceType;

  // Change role using updateAcl (matches AclTabView._handleAclEditRole)
  const onChangeRole = (e) => {
    const newRole = e.target.value;
    dispatch(updateAcl(effectiveResourceType, data, aclItem.type, aclItem.id, newRole));
  };

  // Delete/revoke access using updateAcl (matches AclTabView._handleDeleteAcl)
  const onDelete = () => {
    const effRole = getEffectiveRole(aclItem);
    // For sharer role, pass the role; otherwise pass role for proper deletion
    dispatch(updateAcl(effectiveResourceType, data, aclItem.type, aclItem.id, effRole, true));
  };

  return (
    <div className={styles.aclItemContainer}>
      <Stack>
        <Typography className={styles.aclTitle}>{displayInfo.title}</Typography>
        <Typography className={styles.aclEmail}>{displayInfo.subtitle}</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        {/* Role dropdown - hidden for sharer role (Nexset sharing) or read-only mode */}
        {role !== 'sharer' && !isReadOnly && (
          <Select
            size="small"
            renderItem={renderSelectItem}
            value={role}
            renderValue={(value: string) => ACL_SETTINGS_MAPPING[value]?.title}
            sx={{ minWidth: 180 }}
            items={ACL_ASSIGNMENT_OPTIONS}
            onChange={onChangeRole}
          />
        )}
        {/* Show role as text in read-only mode (for both collaboration roles and sharer) */}
        {isReadOnly && (
          <Typography variant="body2">
            {role === 'sharer' ? 'Sharer' : ACL_SETTINGS_MAPPING[role]?.title || role}
          </Typography>
        )}
        {/* Delete button - hidden in read-only mode */}
        {!isReadOnly && (
          <DeleteIcon
            fontSize="medium"
            htmlColor="#FF7436"
            sx={{ cursor: 'pointer' }}
            onClick={onDelete}
          />
        )}
      </Stack>
    </div>
  );
};

export const Access = (props: AccessProps) => {
  const { alreadyShared, resourceType, data, externalSelection } = props;
  const aclType = aclTypes[resourceType];
  const isNexset = resourceType === RESOURCE_TYPE_DATA_SET;

  const [role, setRole] = useState(!isNexset ? 'collaborator' : 'sharer');
  const [selecteds, setSelecteds] = useState([]);

  const dispatch = useDispatch();
  const org = useCurrentOrg();

  const loading = useIsLoading(UPDATE_ACL_REQUEST);

  // Check if user's current org matches the resource's org
  // Resource org can be in different properties depending on resource type:
  // - Flows: org.id or org_id
  // - DataSets: org (number) or org.id (object)
  // - Sources/Sinks: owner_org or org_id
  const resourceOrgId = (() => {
    // data.org can be a number (org ID) or an object with .id
    if (typeof data?.org === 'number') return data.org;
    if (data?.org?.id) return data.org.id;
    return data?.org_id || data?.owner_org || data?.owner?.org;
  })();
  const isReadOnly = !!resourceOrgId && org?.id !== resourceOrgId;

  const onChangeSelection = (e, data) => {
    setSelecteds(data);
  };

  const onChangeRole = (e) => {
    setRole(e.target.value);
  };

  /**
   * Adds org_id to USER accessors for Nexset sharing.
   * The API requires org_id for dataset sharing operations, but only for USER type.
   * TEAM and ORG types don't need org_id (matches updateAcl behavior in acl/index.js).
   */
  const withOrgId = (accessors: any[]) => {
    if (!isNexset) return accessors;
    return accessors.map((accessor) => ({
      ...accessor,
      ...(accessor.optype === 'USER' && { org_id: org?.id }),
    }));
  };

  const onAdd = () => {
    // Use updateAcl for each item (like AclTabView._handleSetAcl) to properly merge with existing
    // For Nexsets, use DF_DATASET type to enable org_id handling in updateAcl for USER type
    const effectiveAclType = isNexset ? ACL_RESOURCE_TYPE_DF_DATASET : aclType;
    selecteds.forEach((selected) => {
      dispatch(updateAcl(effectiveAclType, data, selected.optype, selected.id, role));
    });
    setSelecteds([]);
  };

  const onChangeAccess = () => {
    dispatch(updateMultipleAcl(aclType, data, withOrgId(externalSelection), role));
  };
  const onDelete = () => {
    dispatch(updateMultipleAcl(aclType, data, withOrgId(externalSelection)));
  };

  // disallow sharing for Nexsets that are not from streaming flows
  if (isNexset && data.flow_type !== flowTypes.streaming) {
    return (
      <Stack padding={2}>
        <Banner
          variant="default"
          message="Nexset Data Share is only available for Flex Flow Nexsets."
        />
      </Stack>
    );
  }

  if (loading) {
    return <CenteredLoader minHeight={85} />;
  }

  const title = isNexset ? 'Share Nexset Data' : 'Invite Others to Collaborate on this resource';

  const subtitle = (() => {
    if (isReadOnly) {
      return 'To modify access controls, switch to the organization that owns this resource.';
    }
    if (isNexset) {
      return 'Owners and Administrators can share this Nexset and its data with other users, teams, or entire organizations.';
    }
    return 'Owners and Administrators can invite other users, teams, or entire organizations on this resource.';
  })();

  return (
    <div>
      <div className={styles.accessDescription}>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2">{subtitle}</Typography>
      </div>
      {/* Hide Grant Access controls when user is not in the resource's org */}
      {!isReadOnly && (
        <div className={styles.shareActions}>
          {!externalSelection || !externalSelection.length ? (
            <AclEntitySelect onChangeSelection={onChangeSelection} />
          ) : null}
          {!isNexset && (
            <Select
              size="small"
              renderItem={renderSelectItem}
              value={role}
              renderValue={(value: string) => {
                return ACL_SETTINGS_MAPPING[value]?.title;
              }}
              sx={{ minWidth: 200 }}
              items={ACL_ASSIGNMENT_OPTIONS}
              onChange={onChangeRole}
            />
          )}
          {!externalSelection || !externalSelection.length ? (
            <Button onClick={onAdd} disabled={!selecteds.length} variant="contained">
              {isNexset ? 'Share Data' : 'Grant Access'}
            </Button>
          ) : (
            <>
              {!isNexset ? (
                <Button onClick={onChangeAccess} variant="outlined">
                  Change Access
                </Button>
              ) : null}
              <Button onClick={onDelete} variant="contained" color="error">
                Remove
              </Button>
            </>
          )}
        </div>
      )}
      <div>
        {alreadyShared.map((aclItem, idx) => (
          <AclItem
            key={aclItem.id || idx}
            aclType={aclType}
            data={data}
            aclItem={aclItem}
            resourceType={aclType}
            isNexset={isNexset}
            isReadOnly={isReadOnly}
          />
        ))}
      </div>
    </div>
  );
};

Access.defaultProps = {
  alreadyShared: [],
};
